import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import SignupPage from '@/pages/signup/index.page';
import type { NavigateToSignupStep } from '@/pages/signup/types';
import { splatForStep } from '@/pages/signup/utils/steps';
import { parseSignupSearch } from '@/pages/signup/utils/signup-search';
import { signupRouteProps } from '@/pages/signup/utils/signup-route-props';
import { useCanonicalSignupUrl } from './-signup-canonical-url';
import { SignupUnknownStepRedirect } from './-signup-redirects';

export const Route = createFileRoute('/signup/$')({
  validateSearch: parseSignupSearch,
  component: SignupRoute,
});

function SignupRoute() {
  const { _splat } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  // The URL is the source of truth for the step, so refresh, browser
  // back/forward, and direct links all land on the right step. The bare
  // /signup is the entry step; unknown segments replace-redirect to it.
  const { step, signupType, promoCode, initialFormValues, resumeEmail } = useMemo(
    () => signupRouteProps(_splat, search),
    [_splat, search],
  );

  const onNavigateStep = useCallback<NavigateToSignupStep>(
    (next, options) => {
      // Preserve search (signupType/promoCode plus marketing attribution
      // params) across step changes. Step advances push so Back walks the
      // funnel; guard bounces pass `replace` so the rejected URL doesn't stay
      // in history for Back to land on again.
      void navigate({
        to: '/signup/$',
        params: { _splat: splatForStep(next) },
        search: (prev) => prev,
        replace: options?.replace,
      });
    },
    [navigate],
  );

  const onCanonicalize = useCallback(
    (canonicalSplat: string) => {
      void navigate({
        to: '/signup/$',
        params: { _splat: canonicalSplat },
        search: (prev) => prev,
        replace: true,
      });
    },
    [navigate],
  );
  useCanonicalSignupUrl({ splat: _splat, step, onCanonicalize });

  if (step == null) {
    return <SignupUnknownStepRedirect />;
  }

  return (
    <SignupPage
      step={step}
      onNavigateStep={onNavigateStep}
      signupType={signupType}
      promoCode={promoCode}
      initialFormValues={initialFormValues}
      resumeEmail={resumeEmail}
    />
  );
}
